// AI-CONTEXT: See docs/features/msi-effect-monetisation.md

/**
 * @typedef {object} MsiScoreLevel
 * @property {number} score - Discrete score level (1..5) from the meetstandaard-API
 * @property {number} monetaryValue - Yearly societal value in euros for this level (can be negative)
 */

/**
 * Maps a (fractional) average score onto euros by linear interpolation
 * between the two adjacent score levels of a meetstandaard-API `scores[]`
 * array. Averages outside the available level range are clamped; levels
 * without a numeric `monetaryValue` are ignored (interpolation spans the gap).
 *
 * @param {number} avgScore - Per-moment average score (e.g. 3.4), negatives already flipped
 * @param {Array<MsiScoreLevel>} scores - `scores[]` for the effect, any order
 * @returns {number | null} Interpolated euro value, or null when input is unusable
 */
const interpolateMonetaryValue = (avgScore, scores) => {
    if (!Number.isFinite(avgScore)) return null

    const levels = (Array.isArray(scores) ? scores : [])
        .map((s) => ({ score: Number(s?.score), monetaryValue: Number(s?.monetaryValue) }))
        .filter((s) => Number.isFinite(s.score) && Number.isFinite(s.monetaryValue))
        .sort((a, b) => a.score - b.score)

    if (levels.length === 0) return null

    const clamped = Math.min(levels[levels.length - 1].score, Math.max(levels[0].score, avgScore))

    // Surrounding levels: greatest score ≤ clamped and smallest score ≥ clamped
    let lower = levels[0]
    let upper = levels[levels.length - 1]
    levels.forEach((level) => {
        if (level.score <= clamped && level.score >= lower.score) lower = level
        if (level.score >= clamped && level.score < upper.score) upper = level
    })

    if (upper.score === lower.score) return lower.monetaryValue

    const fraction = (clamped - lower.score) / (upper.score - lower.score)
    return lower.monetaryValue + fraction * (upper.monetaryValue - lower.monetaryValue)
}

export default interpolateMonetaryValue
